import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { billingApi } from '@/api/billing'
import BillingSnapshotCard from '@/components/billing/BillingSnapshotCard'
import PlanBlock from '@/components/billing/PlanBlock'
import DevicesBlock from '@/components/billing/DevicesBlock'
import StorageBlock from '@/components/billing/StorageBlock'
import AdjustmentsBlock from '@/components/billing/AdjustmentsBlock'
import ChargeHistoryTable from '@/components/billing/ChargeHistoryTable'
import PaymentMethodBlock from '@/components/billing/PaymentMethodBlock'
import { formatNIS } from '@/utils/currency'
import type { LineItem } from '@/types/billing'

interface Props {
  orgId: string
}

export default function OrgBillingTab({ orgId }: Props) {
  const { data: snapshot } = useQuery({
    queryKey: ['admin-billing-snapshot', orgId],
    queryFn: () => billingApi.getSnapshot(orgId),
    staleTime: 60_000,
  })

  const planLine = useMemo(() => {
    const items = snapshot?.line_items ?? []
    const p = items.find((li): li is Extract<LineItem, { type: 'plan' }> => li.type === 'plan')
    return p ?? null
  }, [snapshot])

  return (
    <div dir="ltr" className="space-y-6 text-left max-w-5xl">
      <BillingSnapshotCard orgId={orgId} />

      {snapshot && snapshot.line_items.length > 0 && (
        <div className="card p-6 border border-navy-border bg-[#1a1d27]">
          <h3 className="text-sm font-semibold text-ink-primary mb-3">Current line items</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-ink-muted border-b border-navy-border">
                  <th className="pb-2 pr-3 font-medium">Type</th>
                  <th className="pb-2 pr-3 font-medium">Label</th>
                  <th className="pb-2 text-right font-medium">Amount</th>
                </tr>
              </thead>
              <tbody>
                {snapshot.line_items.map((li, idx) => (
                  <tr key={idx} className="border-b border-navy-border/50 last:border-0">
                    <td className="py-2 pr-3 text-ink-muted uppercase text-xs">{li.type}</td>
                    <td className="py-2 pr-3 text-ink-primary">{'label' in li ? li.label : '—'}</td>
                    <td className="py-2 text-right num font-medium text-ink-primary">
                      {formatNIS(li.amount_agorot)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={2} className="pt-3 text-right text-ink-secondary font-medium">
                    Total
                  </td>
                  <td className="pt-3 text-right text-lg font-bold text-indigo-400 num">
                    {formatNIS(snapshot.monthly_total_agorot)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      <PlanBlock orgId={orgId} planLine={planLine} />
      <PaymentMethodBlock orgId={orgId} />
      <DevicesBlock orgId={orgId} />
      <StorageBlock orgId={orgId} />
      <AdjustmentsBlock orgId={orgId} />
      <ChargeHistoryTable orgId={orgId} />
    </div>
  )
}
